/**
 * "Add to Home Screen" banner for MSRs (owner, 2026-10-30). Rendered inside the signed-in layout only, so login,
 * invitation and account-setup pages never show it. Rules live in utils/installPrompt (msr only, phone/tablet only,
 * not when already installed, 30-day dismissal per device).
 *
 * Android/Chrome: when the browser has offered an install (beforeinstallprompt), one button opens it; otherwise the
 * banner says where the menu item is. iOS: no API exists, so it shows the Share -> Add to Home Screen steps.
 */
import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  clearDeferredInstallPrompt,
  detectPlatform,
  getDeferredInstallPrompt,
  isInstallPromptDismissed,
  isRunningStandalone,
  recordInstallPromptDismissed,
  shouldShowInstallPrompt,
  subscribeInstallPrompt,
  wasAppInstalled,
} from "../../utils/installPrompt";

const ShareIcon: React.FC = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className="inline h-4 w-4 align-text-bottom"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M12 3v12M8 7l4-4 4 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
  </svg>
);

const MsrInstallPrompt: React.FC = () => {
  const { user } = useAuth();
  const platform = detectPlatform(
    typeof navigator === "undefined" ? "" : navigator.userAgent,
    typeof navigator === "undefined" ? 0 : navigator.maxTouchPoints,
  );
  const [dismissed, setDismissed] = useState(() => isInstallPromptDismissed());
  const [, force] = useState(0);
  useEffect(() => subscribeInstallPrompt(() => force((n) => n + 1)), []);

  const show =
    !wasAppInstalled() &&
    shouldShowInstallPrompt({
      role: user?.role,
      platform,
      standalone: isRunningStandalone(),
      dismissed,
    });
  if (!show) {
    return null;
  }

  const dismiss = () => {
    recordInstallPromptDismissed();
    setDismissed(true);
  };

  const deferred = platform === "android" ? getDeferredInstallPrompt() : null;
  const install = async () => {
    if (!deferred) {
      return;
    }
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      clearDeferredInstallPrompt();
      if (choice.outcome === "dismissed") {
        dismiss();
      }
    } catch {
      clearDeferredInstallPrompt();
    }
  };

  return (
    <div
      className="mb-3 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950"
      role="region"
      aria-label="Add to Home Screen"
      data-testid="msr-install-prompt"
      data-platform={platform}
    >
      <img
        src="/icon-192.png"
        alt=""
        className="h-10 w-10 flex-shrink-0 rounded-lg border border-blue-100 bg-white"
      />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">Add Relevate to your Home Screen</p>
        {platform === "ios" && (
          <p className="mt-1" data-testid="msr-install-ios-steps">
            Tap <ShareIcon /> <strong>Share</strong> in Safari, then{" "}
            <strong>Add to Home Screen</strong>. It opens like an app (sign in
            once the first time).
          </p>
        )}
        {platform === "android" && deferred && (
          <button
            type="button"
            onClick={install}
            className="mt-2 min-h-[40px] rounded-md bg-blue-600 px-3 font-semibold text-white hover:bg-blue-700"
            data-testid="msr-install-button"
          >
            Add to Home Screen
          </button>
        )}
        {platform === "android" && !deferred && (
          <p className="mt-1" data-testid="msr-install-android-steps">
            Open the browser menu <strong>⋮</strong> and tap{" "}
            <strong>Add to Home screen</strong> (or <strong>Install app</strong>
            ).
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="-m-1 min-h-[40px] min-w-[40px] flex-shrink-0 rounded p-1 text-lg leading-none text-blue-900 hover:bg-blue-100"
        data-testid="msr-install-dismiss"
      >
        ×
      </button>
    </div>
  );
};

export default MsrInstallPrompt;
